import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api, { formatApiError } from "@/lib/api";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Signup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const strong = password.length >= 8;

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted || !strong) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", { email, name, password });
      // Store credentials briefly so we can auto-login after payment
      sessionStorage.setItem("emos_signup_pending", JSON.stringify({ email, password }));
      navigate(`/pay/${data.token}`, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-ink-0 text-white">
      {/* Left visual */}
      <div className="hidden lg:block relative overflow-hidden border-r border-white/5">
        <img
          src="https://images.unsplash.com/photo-1778849122862-7fa43a7200f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBtaW5pbWFsJTIwdGV4dHVyZSUyMHByZW1pdW18ZW58MHx8fHwxNzg0NTU0NDkwfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,10,11,0.4) 0%, rgba(10,10,11,0.95) 100%)" }} />
        <div className="relative h-full flex flex-col justify-between p-14">
          <Link to="/operationsystem" className="flex items-baseline gap-2 hover:opacity-80" style={{ transitionProperty: "opacity", transitionDuration: "180ms" }}>
            <span className="font-display text-3xl font-semibold tracking-tight">EM</span>
            <span className="font-display text-3xl font-light tracking-tight text-gold">Operating System</span>
          </Link>
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Join the network</p>
            <h1 className="font-display text-4xl font-light tracking-tight leading-tight text-white">
              Your seat at the table<span className="text-gold">.</span>
            </h1>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
              Knowledge, training, downloads, AI assistant &mdash; one year of premium access for 150 USDT.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Full Knowledge Base & Academy access",
                "EM AI grounded on official content",
                "Downloads: slides, PDFs, scripts, kits",
                "Multilingual — NL, EN, ES",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3 text-sm text-zinc-300">
                  <span className="w-5 h-5 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-gold" strokeWidth={2.5} />
                  </span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="relative flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-16">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>

        <div className="max-w-md w-full mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-3" data-testid="signup-eyebrow">Create your account</p>
          <h2 className="font-display text-3xl sm:text-4xl font-light tracking-tight mb-3">
            Step in<span className="text-gold">.</span>
          </h2>
          <p className="text-sm text-zinc-500 mb-10 leading-relaxed">
            One-time 150 USDT payment activates your account for a full year. You&rsquo;ll pay on the next screen.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Full name</label>
              <input
                data-testid="signup-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex de Vries"
                className="em-input"
                required
                minLength={2}
                autoComplete="name"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Email address</label>
              <input
                data-testid="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="em-input"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 block">Password</label>
              <div className="relative">
                <input
                  data-testid="signup-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="em-input pr-10"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password.length > 0 && !strong && (
                <p className="text-[11px] text-amber-400/80 mt-1.5">Password needs at least 8 characters.</p>
              )}
            </div>

            <label className="flex items-start gap-3 text-xs text-zinc-400 leading-relaxed cursor-pointer">
              <input
                data-testid="signup-terms"
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-white/20 bg-white/5 text-gold focus:ring-gold/40"
              />
              <span>
                I understand the one-time 150 USDT (ERC-20) payment activates access for one year and is
                non-refundable. Payment is processed on-chain via my own wallet.
              </span>
            </label>

            {error && (
              <div className="text-xs text-red-400 bg-red-900/20 border border-red-900/40 rounded-lg px-3 py-2" data-testid="signup-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="signup-submit"
              disabled={loading || !accepted || !strong || name.length < 2 || !email}
              className="w-full bg-gold text-ink-0 font-semibold rounded-lg px-6 py-3 hover:bg-gold-hover disabled:opacity-50 flex items-center justify-center gap-2 group"
              style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
            >
              <span>{loading ? "Creating account…" : "Continue to payment"}</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5" style={{ transitionProperty: "transform", transitionDuration: "180ms" }} />
            </button>

            <p className="text-xs text-zinc-500 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-gold hover:text-gold-hover" data-testid="signup-signin-link">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
